import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { notificationActions } from "../store/notificationSlice";
import { apiGet, apiPost, apiDelete } from "./api";
import useSocket from "../hooks/useSocket";
import OrderChat from "./OrderChat";
import DirectChat from "./DirectChat";
import ChatList from "./ChatList";
import AddressInput from "./AddressInput";
import TimePicker from "./TimePicker";
import {
  statusBadge,
  formatWhen,
  formatWallWhen,
  defaultScheduledAt,
  zonedWallToUtc,
} from "./statusLabels";
import TaxiIcon, { IconText } from "./TaxiIcon";
import WaitingInfo from "./WaitingInfo";
import useGeolocation, { isPreciseFix } from "./useGeolocation";

// "±2 км" reads better than "±2000 м" in the precision warning
const accuracyLabel = (meters) =>
  meters >= 1000 ? `${Math.round(meters / 1000)} км` : `${Math.round(meters)} м`;

// Rows of the "Рядом" section: a straight-line estimate, so the copy says
// "по прямой" and never pretends to be a road distance
const NearDistance = ({ distance }) =>
  distance ? (
    <div className="text-cta-icon font-semibold">
      <IconText><TaxiIcon name="pin" size={16} className="inline-block align-[-0.2em]" /> {distance.text} по прямой</IconText>
    </div>
  ) : (
    <div className="text-secondary-text">Расстояние не известно</div>
  );

// Passenger panel, mobile-first: the order composer sits at the BOTTOM of the
// screen like a messenger input; recent orders fill the scrollable area above.
const ACTIVE_STATUSES = ["DRAFT", "PUBLISHED", "ASSIGNED", "ARRIVING", "WAITING", "IN_RIDE", "DISPUTE"];

const PassengerPanel = ({
  me,
  cityId,
  cityTz,
  cityName,
  cityNames,
  refreshMe,
  startCall,
  cityCenter = null,
  training = false,
  initialTab = "new",
}) => {
  const dispatch = useDispatch();
  const { socketListen, socket } = useSocket();
  // Device fix for the "Рядом" section: the nearest drivers, the distance in
  // every card and the pickup point of a new order
  const geo = useGeolocation();
  const [view, setView] = useState(null); // { type: "order" | "direct", id }
  const [orders, setOrders] = useState([]);
  const [ordersFilter, setOrdersFilter] = useState("active");
  const [drivers, setDrivers] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [tab, setTab] = useState(initialTab);
  const [error, setError] = useState(null);
  const [directTarget, setDirectTarget] = useState(null);

  // Композер заказа: маршрут одной строкой, сейчас или на время
  const [route, setRoute] = useState("");
  const [notes, setNotes] = useState("");
  const [whenType, setWhenType] = useState("now");
  // Wall clock of the selected city, not of this device
  const [whenAt, setWhenAt] = useState(() => defaultScheduledAt(cityTz));
  const [timeOpen, setTimeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // Composer is collapsed by default: the form opens on tap and folds back
  // once the order is created (or the user closes it)
  const [composerOpen, setComposerOpen] = useState(false);

  // Status events reach several subscribers at once; keep only the newest
  // response so a slow one cannot overwrite a fresher list.
  const loadSeq = useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current;
    try {
      const ordersRes = await apiGet(`/api/orders?cityId=${cityId}`);
      if (seq !== loadSeq.current) return;
      setOrders(ordersRes.data.orders);
      // The device fix goes in the request: the list returns the distance to
      // each driver and sorts the nearest first
      const point = geo.current();
      // Accuracy goes with the coordinates: the server must know that a
      // network fix is too coarse to support a distance
      const accuracy = Number.isFinite(point?.accuracy) ? `&accuracy=${Math.round(point.accuracy)}` : "";
      const query = point ? `?lat=${point.lat}&lng=${point.lng}${accuracy}` : "";
      const driversRes = await apiGet(`/api/cities/${cityId}/free-drivers${query}`);
      if (seq !== loadSeq.current) return;
      setDrivers(driversRes.data.drivers);
      const favoritesRes = await apiGet("/api/favorites");
      setFavorites(favoritesRes.data.favorites);
    } catch (err) {
      setError(err.message);
    }
  };

  // A point hundreds of kilometres from the city centre is not "рядом": the
  // distances are meaningless there, so the section asks to share the location
  const point = geo.position;
  const inCity =
    !cityCenter ||
    (point &&
      Math.abs(point.lat - cityCenter.lat) < 0.6 &&
      Math.abs(point.lng - cityCenter.lng) < 0.9);
  // Approximate location (Android may grant only "approximate"): the point is
  // a network guess, so distances are hidden until the person allows precise
  // location and taps "Уточнить"
  const coarseFix = geo.status === "ready" && !isPreciseFix(point);
  const nearDrivers = [...drivers].sort((a, b) => {
    const near = a.distance?.meters ?? Infinity;
    const far = b.distance?.meters ?? Infinity;
    return near - far;
  });
  const activeOrders = orders.filter((order) => ACTIVE_STATUSES.includes(order.status));
  const activeOrderIds = activeOrders.map((order) => String(order._id)).join(",");

  // The distance is impossible without a passenger fix, so ask for it as soon
  // as the passenger workspace opens instead of waiting for the "Рядом" tab.
  useEffect(() => {
    if (!training) geo.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the fix and refresh the pickup point of every active order. This
  // also repairs orders created before the passenger shared a location.
  useEffect(() => {
    if (!point || !activeOrders.length) return;
    apiPost("/api/geo/position", point).catch(() => undefined);
    for (const order of activeOrders) {
      socket.emit("passenger:orderLocation", { orderId: order._id, ...point });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point?.lat?.toFixed(5), point?.lng?.toFixed(5), activeOrderIds, cityId, socket]);

  // The first fix arrives after the initial load: refresh so distances appear
  useEffect(() => {
    if (!point) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point?.lat?.toFixed(3), point?.lng?.toFixed(3)]);

  // The driver's fix changed (or the order status did): a distance-bearing
  // event refreshes the lists without a full reload
  useEffect(() => {
    const onDistance = () => load();
    socketListen("order:distance", onDistance);
    socketListen("driver:location", onDistance);
    return () => {
      socket.off("order:distance", onDistance);
      socket.off("driver:location", onDistance);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId]);

  useEffect(() => {
    load();
    const onStatusChanged = (payload) => {
      // Voice and the on-screen notice come from useOrderNotices; refresh data only
      load();
    };
    const onMembershipUpdated = () => refreshMe().catch((err) => {
      if (err.name !== "AbortError") setError(err.message);
    });
    socketListen("order:status", onStatusChanged);
    socketListen("membership:updated", onMembershipUpdated);
    // Events sent while the socket was down are lost: refetch on reconnect
    socketListen("connect", load);
    return () => {
      socket.off("connect", load);
      socket.off("order:status", onStatusChanged);
      socket.off("membership:updated", onMembershipUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityId]);

  // Back from an order: refetch, a missed event must not leave a stale status
  const closeView = () => {
    setView(null);
    load();
  };

  if (view?.type === "order") {
    return (
      <OrderChat
        orderId={view.id}
        me={me}
        cityTz={cityTz}
        onBack={closeView}
        startCall={startCall}
      />
    );
  }
  if (view?.type === "direct") {
    return <DirectChat chatId={view.id} me={me} onBack={closeView} />;
  }

  const submit = async (event) => {
    event.preventDefault();
    if (!route.trim() || sending) return;
    setError(null);
    setSending(true);
    try {
      const pickup = geo.current() ?? (training ? null : await geo.request());
      const res = await apiPost("/api/orders", {
        cityId,
        route: route.trim(),
        ...(training && { training: true }),
        ...(notes.trim() && { notes: notes.trim() }),
        ...(whenType === "scheduled" && {
          whenType: "scheduled",
          // "07:00" on the city clock -> the matching UTC instant
          whenAt: zonedWallToUtc(whenAt, cityTz)?.toISOString(),
        }),
        ...(pickup && { position: pickup }),
        ...(directTarget && {
          targetDriverId: directTarget.driverId,
          chatId: directTarget.chatId,
        }),
      });
      // Close the on-screen keyboard: focus was kept in the field on tap
      document.activeElement?.blur?.();
      setDirectTarget(null);
      setRoute("");
      setNotes("");
      setWhenType("now");
      setWhenAt(defaultScheduledAt(cityTz));
      setTab("orders");
      setComposerOpen(false);
      dispatch(
        notificationActions.addNotification({
          message:
            whenType === "scheduled"
              ? "Заказ на время создан и виден водителям"
              : "Заказ создан, ищем водителя",
          type: "success",
        })
      );
      setView({ type: "order", id: res.data.order._id });
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const callDriver = async (driver) => {
    startCall({
      kind: "direct",
      target: String(driver.driverId),
      cityId,
      name: driver.displayName,
      fromRole: "passenger",
      onAck: ({ chatId }) =>
        setDirectTarget({
          driverId: String(driver.driverId),
          driverName: driver.displayName,
          chatId,
        }),
    });
  };

  const toggleFavorite = async (driver) => {
    try {
      if (driver.isFavorite) {
        await apiDelete(`/api/favorites/${driver.driverId}`);
      } else {
        await apiPost("/api/favorites", { driverId: driver.driverId });
      }
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const driverCard = (driver, extra = {}) => (
    <div key={driver.driverId} className="glass anim-rise rounded-2xl p-3 text-sm text-center">
      <div className="font-semibold text-center">{driver.displayName}</div>
      <div className="text-secondary-text">{driver.carCaption}</div>
       {driver.rating != null && <div><TaxiIcon name="starFilled" className="inline-block align-[-0.2em]" /> {driver.rating}</div>}
      {extra.children}
    </div>
  );

  const routeTitle = (order) =>
    order.to?.text ? `${order.from?.text} → ${order.to?.text}` : order.from?.text;

  // Where the driver is relative to the passenger: refreshed by the server on
  // every status change, so the same field is always up to date
  const orderDistanceLine = (order) => {
    if (order.distance) {
      return (
        <div className="text-[1.3rem] text-cta-icon font-semibold">
          <IconText><TaxiIcon name="pin" size={16} className="inline-block align-[-0.2em]" /> {order.distance.text} до водителя</IconText>
        </div>
      );
    }
    if (order.targetDistance) {
      return (
        <div className="text-[1.3rem] text-cta-icon font-semibold">
          <IconText><TaxiIcon name="pin" size={16} className="inline-block align-[-0.2em]" /> {order.targetDistance.text} по прямой до водителя</IconText>
        </div>
      );
    }
    return null;
  };

  const orderCard = (order) => (
    <button
      key={order._id}
      className="press w-full text-left glass rounded-2xl p-4 text-[1.5rem]"
      onClick={() => setView({ type: "order", id: order._id })}
      aria-label={`Открыть заказ: ${routeTitle(order)}, ${order.status}`}
    >
      <div className="flex items-start gap-2">
        <div className="order-card-title min-w-0 break-words">{routeTitle(order)}</div>
        {order.unreadCount > 0 && (
          <span className="ml-auto bg-danger text-white rounded-full px-2 text-xs shrink-0" aria-label={`Новых сообщений: ${order.unreadCount}`}>
            {order.unreadCount}
          </span>
        )}
      </div>
      {order.driver?.displayName && (
        <div className="text-[1.3rem] text-primary-text">
          <IconText>🚕</IconText> {order.driver.displayName}
          {order.driver.carCaption ? ` · ${order.driver.carCaption}` : ""}
        </div>
      )}
<div className="text-secondary-text mt-1">
         <IconText>{statusBadge(order.status)}</IconText> · {formatWhen(order.when, cityTz)}
      </div>
      {orderDistanceLine(order)}
      {order.waiting && <WaitingInfo waiting={order.waiting} className="mt-1" />}
      <div className="mt-1 text-cta-icon text-[1.2rem] font-semibold">Открыть заказ →</div>
    </button>
  );

  const shownOrders = ordersFilter === "all" ? orders : activeOrders;

  return (
    <div className="flex flex-col h-full max-w-[42rem] mx-auto w-full">
      {/* Табы */}
      <div className="taxi-subnav shrink-0 px-3 pt-3 pb-2 flex gap-2 text-sm overflow-x-auto hide-scrollbar" data-tour="passenger-subtabs">
        {[
          ["new", "🚕 Заказ"],
          ["orders", "📋 Мои заказы"],
          ["drivers", "📍 Рядом"],
          ["favorites", "⭐ Избранные"],
          ["dialogs", "💬 Диалоги"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`taxi-subtab press ${
              tab === key ? "is-active" : ""
            }`}
            aria-current={tab === key ? "page" : undefined}
            onClick={() => setTab(key)}
          >
             <IconText>{label}</IconText>
          </button>
        ))}
      </div>

      {error && <div className="shrink-0 text-danger text-sm text-center px-4">{error}</div>}

      {/* Прокручиваемая область */}
      <div key={tab} className="taxi-panel-enter flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {tab === "new" && (
          <>
            {activeOrders.length > 0 ? (
              <>
                <div className="text-xs text-secondary-text text-center">Ваши активные заказы</div>
                {activeOrders.map(orderCard)}
              </>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 py-10">
                 <TaxiIcon name="route" size={36} className="text-cta-icon" />
                <div className="text-secondary-text text-sm">
                  Напишите маршрут ниже — водители города получат заказ
                </div>
              </div>
            )}
          </>
        )}

        {tab === "orders" && (
          <>
            <div className="flex gap-2 text-sm justify-center">
              {[
                ["active", "Активные"],
                ["all", "Все"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className={`px-3 py-1.5 rounded-full ${
                    ordersFilter === key
                      ? "bg-accent text-black font-semibold"
                      : "bg-secondary text-secondary-text"
                  }`}
                  onClick={() => setOrdersFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            {shownOrders.length === 0 && (
              <div className="text-secondary-text text-sm text-center">Заказов пока нет</div>
            )}
            {shownOrders.map(orderCard)}
          </>
        )}

        {tab === "drivers" && (
          <>
            <div className="text-xs text-secondary-text text-center">
              Свободные водители города, ближайшие — сверху
            </div>
            {geo.status !== "ready" && (
              <div className="glass rounded-2xl p-3 text-sm text-center space-y-2">
                <div className="text-secondary-text">
                  {geo.error ??
                    (geo.supported
                      ? "Поделитесь геопозицией, чтобы видеть, сколько ехать каждому водителю"
                      : "Этот браузер не умеет определять геопозицию")}
                </div>
                {geo.supported && (
                  <button className="press btn-accent px-3 py-2 rounded-xl font-semibold" onClick={() => geo.request()}>
                    📍 Показать, сколько до водителей
                  </button>
                )}
              </div>
            )}
            {geo.status === "ready" && !inCity && (
              <div className="glass rounded-2xl p-3 text-sm text-center text-secondary-text">
                Геопозиция далеко от города {cityName ?? ""}: расстояния не показываются
              </div>
            )}
            {geo.status === "ready" && inCity && coarseFix && (
              <div className="glass rounded-2xl p-3 text-sm text-center space-y-2">
                <div className="text-secondary-text">
                  Геопозиция приблизительная
                  {point?.accuracy ? ` (±${accuracyLabel(point.accuracy)})` : ""}. Разрешите
                  точную: Настройки телефона → Приложения → Chrome или Бомбила →
                  Разрешения → Местоположение → «Точное местоположение».
                </div>
                <button
                  className="press btn-accent px-3 py-2 rounded-xl font-semibold"
                  onClick={() => geo.request()}
                >
                  📍 Уточнить геопозицию
                </button>
              </div>
            )}
            {drivers.length === 0 && (
              <div className="text-secondary-text text-sm text-center">
                Сейчас все водители заняты или не на линии
              </div>
            )}
            {nearDrivers.map((driver) =>
              driverCard(driver, {
                children: (
                  <>
                    {!coarseFix && <NearDistance distance={driver.distance} />}
                    {!coarseFix && driver.positionAt && !driver.distance && (
                      <div className="text-xs text-secondary-text">Водитель не делится позицией</div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <button
                        className="bg-success text-white px-3 py-2 rounded-xl press font-semibold flex-1"
                        onClick={() => callDriver(driver)}
                      >
                        Позвонить
                      </button>
                      <button
                        className="border border-secondary-text/40 px-3 py-2 rounded-lg"
                        onClick={() => toggleFavorite(driver)}
                      >
                         <IconText>{driver.isFavorite ? "★ В избранных" : "☆ В избранные"}</IconText>
                      </button>
                    </div>
                  </>
                ),
              })
            )}
          </>
        )}

        {tab === "favorites" && (
          <>
            {favorites.length === 0 && (
              <div className="text-secondary-text text-sm text-center">Список пуст</div>
            )}
            {favorites.map((driver) =>
              driverCard(driver, {
                children: (
                  <button
                    className="bg-success text-white px-3 py-2 rounded-xl press font-semibold mt-2 w-full"
                    onClick={() => callDriver(driver)}
                  >
                    Позвонить
                  </button>
                ),
              })
            )}
          </>
        )}

        {tab === "dialogs" && (
          <ChatList onOpen={(chatId) => setView({ type: "direct", id: chatId })} />
        )}
      </div>

      {/* Нижний композер заказа: свёрнут по умолчанию, раскрывается по кнопке */}
      {tab === "new" && !composerOpen && (
        <div className="shrink-0 px-3 pb-3 safe-area-bottom">
          <button
            type="button"
            className="press w-full btn-accent rounded-2xl min-h-[5.2rem] px-4 flex items-center justify-between gap-3 font-display font-extrabold text-[1.5rem]"
            data-tour="passenger-new-order"
            onClick={() => setComposerOpen(true)}
            aria-expanded={false}
            aria-controls="order-composer"
          >
            <span className="flex items-center gap-2">
              <TaxiIcon name="route" size={22} />
              Новый заказ
            </span>
            <TaxiIcon name="up" size={20} />
          </button>
        </div>
      )}

      {tab === "new" && composerOpen && (
        <form
          id="order-composer"
          className="shrink-0 glass-strong rounded-t-3xl p-3 space-y-2.5 safe-area-bottom anim-sheet"
          onSubmit={submit}
        >
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="press px-2 py-1 rounded-xl text-secondary-text flex items-center gap-1.5 text-[1.3rem]"
              onClick={() => {
                document.activeElement?.blur?.();
                setComposerOpen(false);
              }}
              aria-expanded
              aria-controls="order-composer"
            >
              <TaxiIcon name="down" size={18} /> Свернуть
            </button>
            {whenType === "now" && (
              <span className="text-[1.2rem] text-secondary-text">Подача: сейчас</span>
            )}
          </div>
          {directTarget && (
            <div className="text-xs text-secondary-text text-center bg-primary rounded-lg p-2">
              Прямой заказ водителю {directTarget.driverName ?? directTarget.driverId}
            </div>
          )}

          <div className="relative flex p-1 rounded-2xl bg-[#0d0d0d]" data-tour="passenger-when">
            {[
              ["now", "⚡ Сейчас"],
              ["scheduled", "🕓 На время"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`press relative z-10 flex-1 py-2 rounded-xl text-[1.3rem] transition-all duration-300 ${
                  whenType === key ? "btn-accent font-bold" : "text-secondary-text"
                }`}
                onClick={() => setWhenType(key)}
              >
                 <IconText>{label}</IconText>
              </button>
            ))}
          </div>

{whenType === "scheduled" && (
            <button
              type="button"
              className="press w-full text-left glass rounded-2xl p-3 text-[1.4rem] flex items-center justify-between gap-2"
              onClick={() => setTimeOpen(true)}
            >
              <span className="flex items-center gap-2">
                 <TaxiIcon name="clock" />
                <span>{formatWallWhen(whenAt, cityTz)}</span>
              </span>
              <span className="text-cta-icon font-semibold">Изменить</span>
            </button>
          )}

<div data-tour="passenger-route">
          <AddressInput
            value={route}
            onChange={setRoute}
            onEnter={submit}
            label="Маршрут"
            placeholder="Куда едем? Например: от ЖД до Центра"
            cityName={cityName}
            cities={cityNames}
            listId="route-suggestions"
          />
          </div>

          {/* Комментарий + кнопка заказа — одна строка в стиле мессенджера:
              при открытой клавиатуре всё помещается над ней */}
          <label className="sr-only" htmlFor="taxi-order-notes">Комментарий водителю (необязательно)</label>
          <div className="flex items-center gap-2 min-w-0">
            <input
              id="taxi-order-notes"
              data-tour="passenger-notes"
              className="flex-1 min-w-0 bg-[#0d0d0d] text-white placeholder:text-secondary-text border border-secondary-text/30 rounded-2xl px-3 py-2.5 focus:border-accent focus:outline-none transition-colors text-[1.5rem] text-primary-text"
              placeholder="Комментарий (необязательно)"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            <button
              aria-label={
                sending
                  ? "Отправляем заказ"
                  : whenType === "scheduled"
                    ? "Заказать на время"
                    : directTarget
                      ? "Оформить заказ водителю"
                      : "Заказать такси"
              }
              data-tour="passenger-send"
              className={`press btn-accent shrink-0 w-[5.6rem] h-[5.6rem] rounded-full flex items-center justify-center ${

                !route.trim() || sending ? "opacity-50" : "opacity-100 anim-pulse-ring"
              }`}
              type="submit"
              // Keep focus in the field on tap: a blur would expand the
              // keyboard-compact layout, move this button and swallow the click
              onMouseDown={(event) => event.preventDefault()}
              disabled={!route.trim() || sending}
            >
              {sending ? (
                <span className="text-[1.6rem] font-bold">…</span>
              ) : (
                <TaxiIcon name="send" size={30} />
              )}
            </button>
          </div>
</form>
      )}

      <TimePicker
        open={timeOpen}
        value={whenAt}
        onChange={setWhenAt}
        onClose={() => setTimeOpen(false)}
        title="Когда подать машину"
        tz={cityTz}
      />
    </div>
  );
};

export default PassengerPanel;


